import type { ReactNode, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Navbar, Nav, Dropdown } from 'react-bootstrap';
import { navbarBreakPoint, topNavbarBreakpoint } from '@/config';
import type { NavbarPosition } from '@/config';
import { useLocation } from 'react-router-dom';
import { useAppContext } from '@/Main';
import { MdCall, MdWhatsapp } from 'react-icons/md';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import Logo from '@/components/common/Logo';
import TopNavRightSideNavItem from './TopNavRightSideNavItem';
import NavbarTopDropDownMenus from './NavbarTopDropDownMenus';
import Flex from '@/components/common/Flex';
import CustomToggle from './CustomToggle';
import { openAiSupportChat, subscribeAiSupportChatOpen } from '@/components/navbar/openAiSupportChat';
import {
  toolIconPrimary,
  toolIconSecondary
} from '@/components/icons/tools/ToolIconFrame';

const NavbarTop = () => {
  const {
    config: { showBurgerMenu, navbarPosition, navbarCollapsed },
    setConfig
  } = useAppContext();

  const { pathname } = useLocation();
  const isChat = pathname.startsWith('/chat') || pathname.startsWith('/mcp/connect');

  const [showDropShadow, setShowDropShadow] = useState(false);

  const handleBurgerMenu = () => {
    setConfig('showBurgerMenu', !showBurgerMenu);
  };

  const setDropShadow = () => {
    const el = document.documentElement;
    if (el.scrollTop > 0) {
      setShowDropShadow(true);
    } else {
      setShowDropShadow(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', setDropShadow);
    return () => window.removeEventListener('scroll', setDropShadow);
  }, []);

  const burgerMenuRef = useRef<HTMLDivElement>(null);

  return (
    <Navbar
      className={classNames('navbar-glass fs--1 navbar-top sticky-kit', {
        'navbar-glass-shadow': showDropShadow && !isChat
      })}
      expand={
        navbarPosition === 'top' ||
        navbarPosition === 'combo' ||
        navbarPosition === 'double-top'
          ? topNavbarBreakpoint
          : true
      }
    >
      <div className="w-100">
        <div className="d-flex flex-between-center">
          <NavbarTopElements
            menus={
              <Navbar.Collapse
                in={navbarCollapsed}
                className="scrollbar py-0 d-none d-lg-block"
              >
                <Nav navbar>
                  <NavbarTopDropDownMenus />
                </Nav>
              </Navbar.Collapse>
            }
            navbarCollapsed={navbarCollapsed}
            navbarPosition={navbarPosition}
            handleBurgerMenu={handleBurgerMenu}
            burgerMenuRef={burgerMenuRef}
          />
        </div>

        <hr className="my-2 d-none d-lg-block" />
      </div>
    </Navbar>
  );
};

type NavbarTopElementsProps = {
  navbarPosition: NavbarPosition;
  handleBurgerMenu: () => void;
  navbarCollapsed: boolean;
  menus: ReactNode;
  burgerMenuRef: RefObject<HTMLDivElement | null>;
};

const NavbarTopElements = ({
  navbarPosition,
  handleBurgerMenu,
  navbarCollapsed,
  menus
}: NavbarTopElementsProps) => {
  const burgerMenuRef = useRef<HTMLDivElement>(null);
  const [supportChatOpen, setSupportChatOpen] = useState(false);

  useEffect(() => subscribeAiSupportChatOpen(setSupportChatOpen), []);

  const iconPrimary = supportChatOpen ? toolIconPrimary : 'currentColor';
  const iconSecondary = supportChatOpen ? toolIconSecondary : 'currentColor';

  return (
    <div className="d-flex w-100 align-items-center flex-wrap">
      <Navbar.Toggle
        ref={burgerMenuRef}
        className={classNames('toggle-icon-wrapper me-md-3 me-2', {
          'd-lg-none':
            navbarPosition === 'top' || navbarPosition === 'double-top',
          [`d-${navbarBreakPoint}-none`]:
            navbarPosition === 'vertical' || navbarPosition === 'combo'
        })}
        as="div"
      >
        <button
          type="button"
          className="navbar-toggler-humburger-icon btn btn-link d-flex flex-center"
          onClick={handleBurgerMenu}
          id="burgerMenu"
        >
          <span className="navbar-toggle-icon">
            <span className="toggle-line" />
          </span>
        </button>
      </Navbar.Toggle>

      <Logo at="navbar-top" width={150} id="topLogo" />
      <div className="d-flex gap-3 align-items-center flex-wrap flex-grow-0">
        <button
          type="button"
          className={classNames(
            'ai-support-nav d-none d-lg-inline-flex align-items-center border border-1 bg-white dark__bg-1000 rounded-3',
            { 'is-open': supportChatOpen }
          )}
          onClick={openAiSupportChat}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={
              supportChatOpen
                ? {
                    ['--icon-primary' as string]:
                      'var(--falcon-navbar-vertical-default-link-active-color, var(--falcon-primary, #00a59c))',
                    ['--icon-secondary' as string]: '#02928b'
                  }
                : undefined
            }
          >
            <path
              fill={iconPrimary}
              stroke="none"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M17.089 6.038C16.639 6 16.057 6 15.2 6H14a1 1 0 1 1 0-2h1.241c.805 0 1.47 0 2.01.044.563.046 1.08.145 1.565.392a4 4 0 0 1 1.748 1.748c.247.485.346 1.002.392 1.564C21 8.29 21 8.954 21 9.758v3.483c0 .805 0 1.47-.044 2.01-.046.562-.145 1.08-.392 1.564a4 4 0 0 1-1.748 1.749c-.485.247-1.002.345-1.564.391-.541.045-1.206.045-2.01.044h-.21l-2.273 2.652a1 1 0 0 1-1.518 0l-2.272-2.652h-.504c-.67 0-1.222 0-1.675-.03-.469-.032-.903-.1-1.32-.274a4 4 0 0 1-2.166-2.165c-.173-.418-.241-.852-.273-1.321C3 14.756 3 14.204 3 13.534V13.5a1 1 0 1 1 2 0c0 .712 0 1.197.026 1.573.025.367.07.558.126.692a2 2 0 0 0 1.083 1.082c.133.055.324.101.692.126.376.026.86.026 1.573.026h.929a1 1 0 0 1 .759.35L12 19.463l1.812-2.114a1 1 0 0 1 .76-.35h.628c.857 0 1.439 0 1.889-.037.438-.036.663-.1.819-.18a2 2 0 0 0 .874-.875c.08-.156.145-.38.18-.819.037-.45.038-1.032.038-1.888V9.8c0-.857 0-1.439-.038-1.889-.035-.438-.1-.663-.18-.819a2 2 0 0 0-.874-.874c-.156-.08-.38-.145-.82-.18"
            />
            <path
              fill={iconSecondary}
              stroke="none"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5.5 0a1 1 0 0 1 .949.684l.967 2.9 2.9.967a1 1 0 0 1 0 1.898l-2.9.967-.967 2.9a1 1 0 0 1-1.898 0l-.967-2.9-2.9-.967a1 1 0 0 1 0-1.898l2.9-.967.967-2.9A1 1 0 0 1 5.5 0m0 4.162-.176.53a1 1 0 0 1-.633.632l-.529.176.53.176a1 1 0 0 1 .632.633l.176.529.176-.53a1 1 0 0 1 .633-.632l.529-.176-.53-.176a1 1 0 0 1-.632-.633zM12 8.4a.6.6 0 0 1 .57.41l.654 1.966 1.966.655a.6.6 0 0 1 0 1.138l-1.966.655-.655 1.966a.6.6 0 0 1-1.138 0l-.655-1.966-1.966-.655a.6.6 0 0 1 0-1.138l1.966-.655.655-1.966A.6.6 0 0 1 12 8.4"
            />
          </svg>
          <span>پشتیبانی با AI</span>
        </button>
        {navbarPosition !== 'vertical' ? (
          menus
        ) : (
          <Flex
            alignItems="center"
            className="gap-3 d-none d-lg-flex"
            wrap="wrap"
          >
            <Link
              target="_blank"
              className="d-flex align-items-center navLinkForceColor"
              to="/prorogate-or-upgrade/"
            >
              قیمت
            </Link>
            <Dropdown>
              <Dropdown.Toggle
                as={CustomToggle}
                className="deactive"
                id="dropdown-basic"
              >
                تماس
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item className="p-1">
                  <div
                    style={{
                      textDecoration: 'none',
                      color: 'unset',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      window.location.href = 'tel:09300208048';
                    }}
                  >
                    <div className="py-1">
                      <div className="px-2 py-1">
                        <MdCall fontSize={17} className="me-2" />
                        تماس مستقیم
                      </div>
                    </div>
                  </div>
                </Dropdown.Item>
                <Dropdown.Item className="p-1">
                  <div
                    style={{
                      textDecoration: 'none',
                      color: 'unset',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      window.open('https://wa.me/989300208048', '_blank');
                    }}
                  >
                    <div className="py-1">
                      <div className="px-2 py-1">
                        <MdWhatsapp fontSize={17} className="me-2" />
                        تماس در واتس آپ
                      </div>
                    </div>
                  </div>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Flex>
        )}
        <style>
          {`
        .navLinkForceColor{
        color: var(--falcon-navbar-vertical-default-link-color) !important;
        }
        .navLinkForceColor:hover{
        color: var(--falcon-navbar-vertical-default-link-hover-color) !important;
        border-color: var(--falcon-navbar-vertical-default-link-hover-color) !important;
        text-decoration:none;
        }
        .navLinkForceColor.nav-link.active{
        color:var(--falcon-navbar-vertical-default-link-active-color) !important;
        border-color: var(--falcon-navbar-vertical-default-link-active-color) !important;
        }
        .ai-support-nav{
        gap: 6px;
        flex: 0 0 auto;
        width: max-content;
        padding: 0.28rem 0.7rem;
        line-height: 1.2;
        white-space: nowrap;
        text-decoration: none !important;
        color: var(--falcon-navbar-vertical-default-link-color) !important;
        font: inherit;
        cursor: pointer;
        }
        .ai-support-nav svg{
        width: 22px !important;
        height: 22px !important;
        min-width: 22px;
        flex: 0 0 22px;
        display: block;
        margin: 0 !important;
        }
        .ai-support-nav:hover,
        .ai-support-nav.is-open{
        color: var(--falcon-navbar-vertical-default-link-hover-color) !important;
        }
        `}
        </style>
      </div>
      <TopNavRightSideNavItem />
    </div>
  );
};

export default NavbarTop;
